"use client";

import { JSX, useEffect, useRef } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import {
  useHoverSelect,
  useLegendHoverSelect,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { importsTokenScope } from "./imports-token-scope";

export type GanttPhase = {
  id: string;
  name: string;
  start: number;
  duration: number;
  progress: number;
  color: string;
};

export type GanttPhasesData = {
  phases: GanttPhase[];
  totalDays?: number;
  currentDay?: number;
  dayLabels?: number[];
};

const DEFAULT_DATA: GanttPhasesData = {
  phases: [
    { id: "planning", name: "Planning", start: 0, duration: 8, progress: 100, color: "var(--chart-1)" },
    { id: "research", name: "User research", start: 4, duration: 10, progress: 100, color: "var(--chart-2)" },
    { id: "design", name: "Design", start: 10, duration: 14, progress: 100, color: "var(--chart-2)" },
    { id: "build_fe", name: "Frontend build", start: 18, duration: 20, progress: 68, color: "var(--chart-3)" },
    { id: "build_be", name: "Backend build", start: 20, duration: 18, progress: 55, color: "var(--chart-3)" },
    { id: "qa", name: "QA & polish", start: 34, duration: 8, progress: 12, color: "var(--chart-4)" },
    { id: "launch", name: "Launch", start: 40, duration: 4, progress: 0, color: "var(--chart-5)" },
  ],
  totalDays: 45,
  currentDay: 22,
  dayLabels: [0, 7, 14, 21, 28, 35, 42],
};

export default function ModuleGanttPhases({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: GanttPhasesData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const phases = data.phases ?? [];
  const totalDays = data.totalDays ?? 45;
  const currentDay = data.currentDay;
  const dayLabels = data.dayLabels ?? [0, 7, 14, 21, 28, 35, 42];

  const legend = useLegendHoverSelect();
  const hoverGesture = useHoverGesture();

  const W = 760;
  const rowH = 34;
  const headerH = 30;
  const footerH = 30;
  const padL = 152;
  const padR = 30;
  const H = headerH + phases.length * rowH + footerH;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [phases]);
  const chartW = W - padL - padR;
  const xFor = (day: number) => padL + (day / totalDays) * chartW;

  const legendItems = phases.map((p) => ({
    id: p.id,
    label: stripInlineMarkdown(p.name),
    color: p.color,
    value: `${p.progress}%`,
  }));
  const legendIndexForPhase = (phaseId: string) =>
    legendItems.findIndex((li) => li.id === phaseId);

  // The legend is this module's selection: one item per phase.
  const phase = useHoverSelect<string>({
    control: {
      selected:
        legend.effective !== null
          ? legendItems[legend.effective]?.id ?? null
          : null,
      select: (id, source) => legend.select(legendIndexForPhase(id), source),
      setSelected: (id) =>
        legend.setSelected(id === null ? null : legendIndexForPhase(id)),
    },
  });
  const effective = phase.effective;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const activePhase = effective
    ? phases.find((p) => p.id === effective) ?? null
    : null;
  const activeIndex = legend.selected;

  useEffect(() => {
    if (activeIndex === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const target = container.querySelector(
      `[data-part-index="${activeIndex}"]`,
    );
    scrollIntoViewX(container, target, { once: false });
  }, [activeIndex]);

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id="gantt-mask" />
          <g mask="url(#gantt-mask)">
            {dayLabels.map((d) => (
              <line
                key={d}
                x1={xFor(d)}
                x2={xFor(d)}
                y1={headerH - 8}
                y2={H - footerH + 4}
                stroke="var(--grid)"
                strokeDasharray="4 4"
                strokeWidth="1"
                fill="none"
              />
            ))}
          </g>

          {dayLabels.map((d) => (
            <text
              key={`h-${d}`}
              x={xFor(d)}
              y={headerH - 14}
              textAnchor="middle"
              fontSize="10.5"
              fill="var(--subtle)"
            >
              Day {d}
            </text>
          ))}

          {currentDay !== undefined && (
            <>
              <line
                x1={xFor(currentDay)}
                x2={xFor(currentDay)}
                y1={headerH - 6}
                y2={H - footerH + 8}
                stroke="var(--module-accent)"
                strokeWidth="1"
                opacity="0.65"
              />
              <text
                x={xFor(currentDay)}
                y={headerH - 6}
                textAnchor="middle"
                fontSize="9.5"
                fill="var(--foreground)"
                style={{
                  letterSpacing: "0.15em",
                }}
                dy="-2"
              >
                {t("NOW")}
              </text>
            </>
          )}

          {phases.map((p, i) => {
            const y = headerH + i * rowH;
            const barY = y + 8;
            const barH = 16;
            const x1 = xFor(p.start);
            const x2 = xFor(p.start + p.duration);
            const barW = x2 - x1;
            const progressW = barW * (p.progress / 100);
            const isHovered = effective === p.id;
            const dim = effective !== null && !isHovered;

            return (
              <g
                key={p.id}
                ref={(el) => {
                  anchorRefs.current[p.id] = el;
                }}
                data-module-type="gantt-phases"
                data-part-index={i}
                data-part-label={p.name}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) phase.setHovered(p.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) phase.setHovered(null);
                }}
                onClick={(e) =>
                  phase.select(p.id, { kind: "segment", id: p.id, element: e.currentTarget })
                }
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.4 : 1}
              >
                {isHovered && (
                  <rect
                    x={padL - 10}
                    y={y + 2}
                    width={chartW + 20}
                    height={rowH - 4}
                    fill="var(--foreground)"
                    fillOpacity="0.03"
                    rx="4"
                  />
                )}
                <foreignObject
                  x={4}
                  y={barY - 4}
                  width={padL - 18}
                  height={24}
                  overflow="visible"
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      height: "100%",
                      color: isHovered ? "var(--foreground)" : "var(--muted-foreground)",
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: 1.15,
                      textAlign: "right",
                      overflowWrap: "anywhere",
                      transition: "color 0.15s",
                    }}
                  >
                    {stripInlineMarkdown(p.name)}
                  </div>
                </foreignObject>
                <rect
                  x={x1}
                  y={barY}
                  width={barW}
                  height={barH}
                  rx="3"
                  fill="var(--card)"
                  stroke={p.color}
                  strokeWidth="1"
                  strokeDasharray={p.progress === 0 ? "3 3" : "0"}
                  opacity={p.progress === 0 ? 0.6 : 1}
                />
                {p.progress > 0 && (
                  <rect
                    x={x1}
                    y={barY}
                    width={progressW}
                    height={barH}
                    rx="3"
                    fill={p.color}
                    opacity={p.progress === 100 ? 0.85 : 1}
                    style={{ transition: "width 0.4s" }}
                  />
                )}
                <text
                  x={x2 + 8}
                  y={barY + 12}
                  fontSize="10.5"
                  fill={isHovered ? "var(--foreground)" : "var(--subtle)"}
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    transition: "fill 0.15s",
                  }}
                >
                  {p.progress}%
                </text>
              </g>
            );
          })}

          {dayLabels.map((d, i) => (
            <text
              key={`f-${d}`}
              x={xFor(d)}
              y={H - 10}
              textAnchor="middle"
              fontSize="10.5"
              fill="var(--subtle)"
            >
              {i === 0 ? t("Day {day}", { day: 0 }) : t("+{days}d", { days: d })}
            </text>
          ))}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activePhase !== null}
        anchor={activePhase ? anchorRefs.current[activePhase.id] ?? null : null}
        contentKey={activePhase?.id}
        title={stripInlineMarkdown(activePhase?.name) || undefined}
        rows={
          activePhase
            ? [
                {
                  color: activePhase.color,
                  label: t("Start"),
                  value: t("Day {day}", { day: activePhase.start }),
                },
                {
                  color: activePhase.color,
                  label: t("Duration"),
                  value: t("{days}d", { days: activePhase.duration }),
                },
                { color: activePhase.color, label: t("Progress"), value: `${activePhase.progress}%` },
              ]
            : []
        }
      />

      {showLegend && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={legend.hovered}
            onHoverChange={legend.setHovered}
            selectedIndex={legend.selected}
            onSelect={legend.select}
            onLabelRef={legend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
